import { useMemo, type ComponentType } from "react";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Chip,
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Typography
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import LockResetIcon from "@mui/icons-material/LockReset";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import * as FlagIcons from "country-flag-icons/react/3x2";
import { formatDateTime, formatLastSeen } from "../utils/users";
import type { UserGroupSummary, UserSummary } from "../types/user";

type UserCardsProps = {
  users: UserSummary[];
  isAdmin: boolean;
  availableUserGroups: UserGroupSummary[];
  userGroupMap: Map<string, string>;
  isLoadingUserGroups: boolean;
  isUserGroupsError: boolean;
  userGroupsErrorMessage: string | null;
  updatingUserId: string | null;
  deletingUserId: string | null;
  onRoleChange: (event: SelectChangeEvent<string>, user: UserSummary) => void;
  onUserGroupsChange: (event: SelectChangeEvent<string[]>, user: UserSummary) => void;
  onResetPassword: (user: UserSummary) => void;
  onEdit: (user: UserSummary) => void;
  onDelete: (user: UserSummary) => void;
};

const UserCards = ({
  users,
  isAdmin,
  availableUserGroups,
  userGroupMap,
  isLoadingUserGroups,
  isUserGroupsError,
  userGroupsErrorMessage,
  updatingUserId,
  deletingUserId,
  onRoleChange,
  onUserGroupsChange,
  onResetPassword,
  onEdit,
  onDelete
}: UserCardsProps) => {
  const regionNames = useMemo(
    () =>
      typeof Intl !== "undefined" && typeof Intl.DisplayNames !== "undefined"
        ? new Intl.DisplayNames(["en"], { type: "region" })
        : null,
    []
  );

  return (
    <Stack spacing={2}>
      {users.map((user) => {
        const countryCode = user.country?.trim().toUpperCase();
        const Flag = countryCode
          ? (FlagIcons as Record<string, ComponentType<{ width?: number }> | undefined>)[countryCode]
          : null;
        const countryLabel = countryCode ? regionNames?.of(countryCode) ?? countryCode : "";
        const createdAtLabel = formatDateTime(user.createdAt);
        const lastSeenLabel = formatLastSeen(user.lastSeen);

        return (
          <Card key={user.id} variant="outlined">
            <CardContent>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between">
                <Box>
                  <Typography variant="h6">{user.username}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user.email}
                  </Typography>
                  {countryCode ? (
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                      {Flag ? <Flag width={20} /> : null}
                      <Typography variant="body2">
                        {countryLabel} ({countryCode})
                      </Typography>
                    </Stack>
                  ) : null}
                  {(createdAtLabel || lastSeenLabel) ? (
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 0.5 }}>
                      {createdAtLabel ? (
                        <Typography variant="body2" color="text.secondary">
                          Created: {createdAtLabel}
                        </Typography>
                      ) : null}
                      {createdAtLabel && lastSeenLabel ? (
                        <Typography variant="body2" color="text.secondary" component="span">
                          •
                        </Typography>
                      ) : null}
                      {lastSeenLabel ? (
                        <Typography variant="body2" color="text.secondary">
                          Last seen: {lastSeenLabel}
                        </Typography>
                      ) : null}
                    </Stack>
                  ) : null}
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel id={`role-label-${user.id}`}>Role</InputLabel>
                    <Select
                      labelId={`role-label-${user.id}`}
                      label="Role"
                      value={user.role}
                      onChange={(event) => onRoleChange(event, user)}
                      disabled={updatingUserId === user.id}
                    >
                      <MenuItem value="admin">Admin</MenuItem>
                      <MenuItem value="user">User</MenuItem>
                    </Select>
                  </FormControl>
                  <Chip label={user.active ? "Active" : "Disabled"} color={user.active ? "success" : "default"} variant="outlined" />
                </Stack>
              </Stack>
              {isAdmin && user.role !== "admin" ? (
                <Box mt={2}>
                  <FormControl
                    fullWidth
                    size="small"
                    disabled={isLoadingUserGroups || updatingUserId === user.id}
                  >
                    <InputLabel id={`groups-label-${user.id}`}>User groups</InputLabel>
                    <Select<string[]>
                      multiple
                      labelId={`groups-label-${user.id}`}
                      label="User groups"
                      value={user.userGroups ?? []}
                      onChange={(event: SelectChangeEvent<string[]>) => onUserGroupsChange(event, user)}
                      renderValue={(selected) => {
                        const values = Array.isArray(selected) ? selected : [selected];
                        if (values.length === 0) {
                          return "None";
                        }
                        return values
                          .map((value) => {
                            const id = String(value);
                            return userGroupMap.get(id) ?? id;
                          })
                          .join(", ");
                      }}
                    >
                      {availableUserGroups.length === 0 ? (
                        <MenuItem disabled value="__none">
                          No user groups
                        </MenuItem>
                      ) : (
                        availableUserGroups.map((group) => (
                          <MenuItem key={group.id} value={group.id}>
                            <Checkbox checked={user.userGroups?.includes(group.id) ?? false} />
                            <ListItemText primary={group.name} />
                          </MenuItem>
                        ))
                      )}
                    </Select>
                    {isLoadingUserGroups ? (
                      <FormHelperText>Loading user groups…</FormHelperText>
                    ) : isUserGroupsError ? (
                      <FormHelperText error>{userGroupsErrorMessage ?? "Unable to load user groups."}</FormHelperText>
                    ) : availableUserGroups.length === 0 ? (
                      <FormHelperText>No user groups available.</FormHelperText>
                    ) : null}
                  </FormControl>
                </Box>
              ) : (
                <Box mt={2} display="flex" flexWrap="wrap" gap={1}>
                  {user.role === "admin" ? (
                    <Typography variant="body2" color="text.secondary">
                      Admins have access to all user groups.
                    </Typography>
                  ) : user.userGroups?.length ? (
                    user.userGroups.map((groupId) => (
                      <Chip key={groupId} label={userGroupMap.get(groupId) ?? groupId} size="small" variant="outlined" />
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      Not assigned to any user groups.
                    </Typography>
                  )}
                </Box>
              )}
            </CardContent>
            <CardActions>
              <Button size="small" variant="outlined" startIcon={<LockResetIcon />} onClick={() => onResetPassword(user)}>
                Reset password
              </Button>
              <Button
                size="small"
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => onEdit(user)}
              >
                Edit
              </Button>
              <Button
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
                variant="outlined"
                onClick={() => onDelete(user)}
                disabled={deletingUserId === user.id}
              >
                Delete
              </Button>
            </CardActions>
          </Card>
        );
      })}
    </Stack>
  );
};

export default UserCards;
